import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Send, Hash, Plus, Link2, MessagesSquare, Wifi, WifiOff } from "lucide-react";
import api, { formatApiError, wsUrl } from "@/lib/api";
import { STATUS_COLOR, initials, timeAgo } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

function LinkTaskPopover({ message, tasks, projectId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const link = useMutation({
    mutationFn: async (taskId) => (await api.put(`/messages/${message.id}/link-task`, { context_task_id: taskId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", message.channel_id] });
      setOpen(false);
      toast.success("Message linked");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
          data-testid={`link-task-btn-${message.id}`} title="Link to task">
          <Link2 className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-card border-border w-72 p-2 max-h-72 overflow-y-auto">
        <div className="label-caps px-2 py-1.5">Pin message to task</div>
        {message.context_task && (
          <button onClick={() => link.mutate(null)} className="w-full text-left px-2 py-2 rounded hover:bg-destructive/10 text-sm text-destructive">
            Unlink current task
          </button>
        )}
        {tasks.length === 0 && <div className="px-2 py-3 text-sm text-muted-foreground">No tasks yet.</div>}
        {tasks.map((t) => (
          <button key={t.id} onClick={() => link.mutate(t.id)} data-testid={`link-to-task-${t.id}`}
            className="w-full text-left px-2 py-2 rounded hover:bg-background text-sm flex items-center gap-2">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLOR[t.status] }} />
            <span className="truncate">{t.title}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function ChannelsTab({ projectId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [activeChannel, setActiveChannel] = useState(null);
  const [body, setBody] = useState("");
  const [linkTaskId, setLinkTaskId] = useState("");
  const [newChannel, setNewChannel] = useState("");
  const [wsLive, setWsLive] = useState(false);
  const scrollRef = useRef();

  const { data: channels } = useQuery({
    queryKey: ["channels", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/channels`)).data.channels,
  });

  const { data: tasks } = useQuery({
    queryKey: ["tasks", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/tasks`)).data.tasks,
  });

  useEffect(() => {
    if (channels && channels.length && !activeChannel) setActiveChannel(channels[0].id);
  }, [channels, activeChannel]);

  const { data: messages } = useQuery({
    queryKey: ["messages", activeChannel],
    queryFn: async () => (await api.get(`/channels/${activeChannel}/messages`)).data.messages,
    enabled: !!activeChannel,
    refetchInterval: 6000, // polling fallback
  });

  // WebSocket live layer
  useEffect(() => {
    if (!activeChannel) return;
    let ws;
    try {
      ws = new WebSocket(wsUrl(activeChannel));
      ws.onopen = () => setWsLive(true);
      ws.onclose = () => setWsLive(false);
      ws.onerror = () => setWsLive(false);
      ws.onmessage = () => qc.invalidateQueries({ queryKey: ["messages", activeChannel] });
    } catch {
      setWsLive(false);
    }
    return () => { try { ws && ws.close(); } catch {} };
  }, [activeChannel, qc]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const createChannel = useMutation({
    mutationFn: async () => (await api.post(`/projects/${projectId}/channels`, { name: newChannel, is_private: false })).data.channel,
    onSuccess: (ch) => {
      qc.invalidateQueries({ queryKey: ["channels", projectId] });
      setNewChannel(""); setActiveChannel(ch.id);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const send = useMutation({
    mutationFn: async () => (await api.post(`/channels/${activeChannel}/messages`, {
      body, context_task_id: linkTaskId || null,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", activeChannel] });
      setBody(""); setLinkTaskId("");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-4 h-[68vh]">
      {/* Channel list */}
      <div className="border border-border rounded-md bg-card flex flex-col">
        <div className="px-3 h-11 flex items-center border-b border-border label-caps !text-foreground">Channels</div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {(channels || []).map((c) => (
            <button key={c.id} onClick={() => setActiveChannel(c.id)} data-testid={`channel-${c.id}`}
              className={`w-full flex items-center gap-2 px-3 h-11 rounded-md font-medium transition-colors ${
                activeChannel === c.id ? "bg-primary text-primary-foreground" : "hover:bg-background text-muted-foreground"
              }`}>
              <Hash className="h-4 w-4" /> <span className="truncate">{c.name}</span>
            </button>
          ))}
        </div>
        <div className="p-2 border-t border-border flex gap-1.5">
          <Input value={newChannel} onChange={(e) => setNewChannel(e.target.value)} placeholder="new-channel"
            data-testid="new-channel-input" className="h-11 bg-background text-sm" />
          <Button size="icon" className="h-11 w-11 bg-primary text-primary-foreground shrink-0" disabled={!newChannel || createChannel.isPending}
            onClick={() => createChannel.mutate()} data-testid="create-channel-button"><Plus className="h-5 w-5" /></Button>
        </div>
      </div>

      {/* Messages */}
      <div className="border border-border rounded-md bg-[hsl(var(--sidebar))] flex flex-col min-h-0">
        <div className="px-4 h-11 flex items-center justify-between border-b border-border">
          <span className="font-bold flex items-center gap-2"><Hash className="h-4 w-4 text-primary" />
            {channels?.find((c) => c.id === activeChannel)?.name || "…"}</span>
          <span className={`flex items-center gap-1.5 text-xs ${wsLive ? "text-[#10B981]" : "text-muted-foreground"}`} data-testid="ws-status">
            {wsLive ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            {wsLive ? "Live" : "Polling"}
          </span>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
          {!messages ? (
            <div className="grid place-items-center h-full"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : messages.length === 0 ? (
            <div className="grid place-items-center h-full text-muted-foreground">
              <div className="text-center"><MessagesSquare className="h-10 w-10 mx-auto mb-2" />No messages yet.</div>
            </div>
          ) : messages.map((m) => (
            <div key={m.id} className="group flex gap-3" data-testid={`message-${m.id}`}>
              <div className="h-9 w-9 rounded bg-secondary grid place-items-center text-xs font-data font-bold shrink-0">
                {initials(m.sender?.first_name, m.sender?.last_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-sm">{m.sender?.first_name} {m.sender?.last_name}</span>
                  <span className="text-xs text-muted-foreground font-data">{timeAgo(m.created_at)}</span>
                  <LinkTaskPopover message={m} tasks={tasks || []} projectId={projectId} />
                </div>
                <div className="text-sm mt-0.5 break-words">{m.body}</div>
                {m.context_task && (
                  <div className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded bg-primary/15 border border-primary/40 text-xs" data-testid={`msg-context-${m.id}`}>
                    <Link2 className="h-3 w-3 text-primary" />
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: STATUS_COLOR[m.context_task.status] }} />
                    <span className="font-semibold text-primary">{m.context_task.title}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Composer */}
        <div className="border-t border-border p-3 space-y-2">
          {linkTaskId && (
            <div className="flex items-center gap-2 text-xs text-primary">
              <Link2 className="h-3.5 w-3.5" /> Linking to: {tasks?.find((t) => t.id === linkTaskId)?.title}
              <button onClick={() => setLinkTaskId("")} className="text-muted-foreground hover:text-destructive ml-1">clear</button>
            </div>
          )}
          <div className="flex gap-2">
            <Textarea value={body} onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (body.trim()) send.mutate(); } }}
              placeholder="Message the crew… (Enter to send)" data-testid="message-input"
              className="bg-background min-h-12 max-h-32 resize-none" />
            <div className="flex flex-col gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="secondary" size="icon" className="h-12 w-12" data-testid="composer-link-task"><Link2 className="h-5 w-5" /></Button>
                </PopoverTrigger>
                <PopoverContent className="bg-card border-border w-64 p-2 max-h-64 overflow-y-auto">
                  <div className="label-caps px-2 py-1.5">Attach task context</div>
                  {(tasks || []).map((t) => (
                    <button key={t.id} onClick={() => setLinkTaskId(t.id)}
                      className="w-full text-left px-2 py-2 rounded hover:bg-background text-sm flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLOR[t.status] }} />
                      <span className="truncate">{t.title}</span>
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
              <Button size="icon" className="h-12 w-12 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={!body.trim() || send.isPending} onClick={() => send.mutate()} data-testid="send-message-button">
                {send.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
